import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const WINDOW_OPTIONS = [
  { key: "24h", label: "Last 24 hours" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

const PROVIDER_LABEL = {
  anthropic: "Claude",
  openai: "OpenAI",
  gemini: "Gemini",
};

const fmtUSD = (n) => {
  const v = Number(n || 0);
  if (v === 0) return "$0.00";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
};

const fmtInt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function LlmCostPage() {
  const { user } = useAuth();
  const [window, setWindow] = useState("30d");
  const [propertyId, setPropertyId] = useState(""); // "" = all properties

  const { data: propsData } = useQuery({
    queryKey: ["admin-properties"],
    queryFn: () => api.adminListProperties(),
    enabled: user?.role === "admin",
  });
  // adminListProperties returns the array directly (not wrapped).
  const properties = Array.isArray(propsData) ? propsData : [];

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-llm-cost", window, propertyId],
    queryFn: () => api.adminGetLlmCost(window, propertyId || null),
    enabled: user?.role === "admin",
    refetchInterval: 30000, // background refresh every 30s
  });

  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">Not authorized</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">
          Only admins can access this page.
        </div>
      </div>
    );
  }

  const totalCost = data?.total_cost ?? 0;
  const totalCalls = data?.total_calls ?? 0;
  const byModel = data?.by_model || [];
  const byTask = data?.by_task || [];
  const breakdown = data?.breakdown || [];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline flex items-center gap-2">
              <DollarSign className="w-3 h-3" />
              Admin
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">
              LLM Cost
            </h1>
            <div className="text-xs sm:text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">
              Approximate spend by model, task, and property. Numbers are
              estimates from published provider list prices — actual
              Emergent Universal Key billing may differ. Tracking is
              forward-only from the day usage logging was enabled.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="llm-cost-property"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="px-3 py-2 text-sm border border-[var(--border)] bg-white font-mono-display"
              title="Filter by property"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              data-testid="llm-cost-window"
              value={window}
              onChange={(e) => setWindow(e.target.value)}
              className="px-3 py-2 text-sm border border-[var(--border)] bg-white font-mono-display"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center gap-2 text-[var(--ink-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        )}
        {error && (
          <div className="text-red-600 text-sm">
            Failed to load: {error.message}
          </div>
        )}
        {data && (
          <div className="max-w-6xl space-y-8">
            {/* Top-level totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Total cost" value={fmtUSD(totalCost)} />
              <StatCard label="Total calls" value={fmtInt(totalCalls)} />
              <StatCard
                label="Models used"
                value={new Set(byModel.map((m) => `${m.provider}|${m.model}`)).size}
              />
              <StatCard
                label="Tasks tracked"
                value={new Set(byTask.map((t) => t.prompt_type)).size}
              />
            </div>

            {totalCalls === 0 && (
              <div className="p-6 border border-[var(--border)] bg-white flex items-start gap-3">
                <Info className="w-4 h-4 mt-0.5 text-[var(--ink-muted)] shrink-0" />
                <div className="text-sm text-[var(--ink-muted)]">
                  No LLM calls recorded in this window yet. Cost tracking
                  started when this feature shipped — historical calls
                  aren&apos;t counted. Run a research, article, or image
                  generation and the numbers will populate here within a
                  few seconds.
                </div>
              </div>
            )}

            {/* Two side-by-side summaries */}
            <div className="grid lg:grid-cols-2 gap-6">
              <SummaryTable
                title="By model"
                rows={byModel}
                columns={[
                  { key: "model", label: "Model",
                    render: (r) => (
                      <div>
                        <div className="font-serif-display leading-tight">{r.model}</div>
                        <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
                          {PROVIDER_LABEL[r.provider] || r.provider}
                        </div>
                      </div>
                    ),
                  },
                  { key: "calls", label: "Calls", render: (r) => fmtInt(r.calls), align: "right" },
                  { key: "cost_usd", label: "Cost", render: (r) => fmtUSD(r.cost_usd), align: "right", bold: true },
                ]}
              />
              <SummaryTable
                title="By task"
                rows={byTask}
                columns={[
                  { key: "prompt_type", label: "Task",
                    render: (r) => (
                      <div className="font-serif-display leading-tight">{r.prompt_type}</div>
                    ),
                  },
                  { key: "calls", label: "Calls", render: (r) => fmtInt(r.calls), align: "right" },
                  { key: "cost_usd", label: "Cost", render: (r) => fmtUSD(r.cost_usd), align: "right", bold: true },
                ]}
              />
            </div>

            {/* Detailed breakdown */}
            <div>
              <div className="overline mb-2">
                Detailed breakdown — property × task × model
              </div>
              <div className="border border-[var(--border)] bg-white overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--secondary)]/40 text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
                      <th className="text-left px-3 py-2">Property</th>
                      <th className="text-left px-3 py-2">Task</th>
                      <th className="text-left px-3 py-2">Model</th>
                      <th className="text-right px-3 py-2">Calls</th>
                      <th className="text-right px-3 py-2">Input tok</th>
                      <th className="text-right px-3 py-2">Output tok</th>
                      <th className="text-right px-3 py-2">Images</th>
                      <th className="text-right px-3 py-2">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-3 py-6 text-center text-[var(--ink-muted)] text-xs"
                        >
                          No entries yet.
                        </td>
                      </tr>
                    )}
                    {breakdown.map((r, i) => (
                      <tr
                        key={`${r.property_id}-${r.prompt_type}-${r.provider}-${r.model}-${i}`}
                        className={
                          i !== breakdown.length - 1
                            ? "border-b border-[var(--border)]"
                            : ""
                        }
                      >
                        <td className="px-3 py-2">
                          {r.property_name || (
                            <span className="text-[var(--ink-muted)] italic">
                              (none)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono-display text-[11px]">
                          {r.prompt_type}
                        </td>
                        <td className="px-3 py-2 font-mono-display text-[11px]">
                          {r.model}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.calls)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.input_tokens)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {fmtInt(r.output_tokens)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-display text-[11px]">
                          {r.images ? fmtInt(r.images) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-serif-display">
                          {fmtUSD(r.cost_usd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
              Refreshes every 30s · Prices are provider list rates as of
              Feb 2026 · OpenAI/Gemini token counts are estimated
              (chars ÷ 4) since the SDK does not expose exact usage
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-[var(--border)] bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
        {label}
      </div>
      <div className="text-2xl font-serif-display leading-tight mt-1">
        {value}
      </div>
    </div>
  );
}

function SummaryTable({ title, rows, columns }) {
  return (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="border border-[var(--border)] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--secondary)]/40 text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-4 text-center text-[var(--ink-muted)] text-xs"
                >
                  No entries yet.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={i}
                className={
                  i !== rows.length - 1
                    ? "border-b border-[var(--border)]"
                    : ""
                }
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"} ${
                      c.bold ? "font-serif-display" : ""
                    }`}
                  >
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
